import React, { useEffect, useState, useCallback } from 'react'
import { Formik, Form } from 'formik'
import moment from 'moment'
import { toast } from 'react-toastify'
import { Flex } from 'reflexbox'
import { useParams } from 'react-router-dom'

import { SelectField, DateRangePickerField } from 'components/Form'
import Button from 'components/Button'
import DataListingTable from 'components/DataListingTable'

import { getListOfOrderCards, getCardTypes } from 'services/giftCards'

const tableColumns = [
  {
    Header: 'Cartão',
    accessor: 'description',
  },
  {
    Header: 'Código do cartão',
    accessor: 'code',
  },
  {
    Header: 'Valido de',
    accessor: 'initial_date',
  },
  {
    Header: 'Valido até',
    accessor: 'expiration_date',
  },
  {
    Header: 'Status',
    accessor: 'status',
  },
  {
    Header: 'Data de utilização',
    accessor: 'used_at',
  },
]

const GiftCardComsumptionList = () => {
  const [tableData, setTableData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [sortBy, setSortBy] = useState(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })
  const [filters, setFilters] = useState(null)
  const [giftCardPagination, setGiftCardPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })

  const { id: orderId } = useParams()

  const handleSubmit = values =>
    setFilters({
      ...(values?.status && { status: values?.status?.value }),
      ...(values?.description && { description: values?.description?.value }),
      ...(values?.date?.startDate && { first_date: moment(values?.date?.startDate).format('YYYY-MM-DD') }),
      ...(values?.date?.endDate && { last_date: moment(values?.date?.endDate).format('YYYY-MM-DD') }),
    })

  const loadGiftCardOptions = async search => {
    try {
      if (search) {
        setGiftCardPagination({ page: 1, limit: 10, ...giftCardPagination })
      }

      const res = await getCardTypes({ ...giftCardPagination, ...(search && { search }), orderId })
      const formattedData = res.cards.map(card => ({ label: card.description, value: card.description }))

      if (res.pagination.page !== res.pagination.total_pages)
        setGiftCardPagination({ page: res.pagination.page + 1, limit: res.pagination.limit, ...res.pagination })

      return {
        options: formattedData,
        hasMore: search ? false : res.pagination.page !== res.pagination.total_pages,
      }
    } catch (e) {
      console.log(e)
      toast.error('Aconteceu um erro ao carregar os dados dos cartões presente. Por favor tente novamente.')
    }
  }

  const handleTableDataFetch = ({ sortBy, currentPage, pageSize }) => {
    setSortBy(sortBy)
    setPagination({ ...pagination, currentPage, pageSize })
  }

  const fetchCards = useCallback(async () => {
    try {
      setIsLoading(true)

      const handleCardStatus = status => {
        switch (status) {
          case 'expired':
            return 'Expirado'
          case 'inactive':
            return 'Inativo'
          case 'used':
            return 'Utilizado'
          case 'not_used':
            return 'Não utilizado'
          default:
            return '-'
        }
      }

      const res = await getListOfOrderCards({ id: orderId, sortBy, pagination, ...filters })

      setTableData(
        res.cards.map(card => ({
          ...card,
          initial_date: card.initial_date ? moment(card.initial_date).format('DD/MM/YYYY') : '-',
          expiration_date: card.expiration_date ? moment(card.expiration_date).format('DD/MM/YYYY') : '-',
          status: handleCardStatus(card.status),
          used_at: card.used_at ? moment(card.used_at).format('DD/MM/YYYY') : '-',
        })),
      )
      setPagination(res.pagination)
    } catch (e) {
      console.log(e)
      toast.error('Ocorreu um erro ao acessar a lista de cartões, por favor tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }, [pagination.currentPage, pagination.pageSize, sortBy, filters])

  useEffect(() => {
    if (sortBy) {
      fetchCards()
    }
  }, [pagination.currentPage, pagination.pageSize, sortBy])

  useEffect(() => {
    if (pagination.currentPage === 1 && filters) {
      fetchCards()
    }

    setPagination({ ...pagination, currentPage: 1 })
  }, [filters])

  return (
    <Flex flexDirection='column'>
      <Formik
        initialValues={{
          description: undefined,
          status: undefined,
          date: { startDate: undefined, endDate: undefined },
        }}
        onSubmit={handleSubmit}
      >
        <Flex as={Form} mt={32} mb={24} alignItems='center'>
          <SelectField
            name='description'
            label='Cartão'
            placeholder='Cartão'
            loadOptions={loadGiftCardOptions}
            minWidth={180}
            mr={24}
          />
          <DateRangePickerField name='date' label='Validade' mr={24} />
          <SelectField
            name='status'
            label='Status'
            placeholder='Status'
            options={[
              { label: 'Utilizado', value: 'used' },
              { label: 'Não utilizado', value: 'not_used' },
              { label: 'Expirado', value: 'expired' },
              { label: 'Inativo', value: 'inactive' },
            ]}
            minWidth={180}
            mr={24}
          />
          <Button variant='secondary' disabled={isLoading}>
            Filtrar
          </Button>
        </Flex>
      </Formik>

      <DataListingTable
        columns={tableColumns}
        data={tableData}
        loading={isLoading}
        fetchData={handleTableDataFetch}
        pagination={pagination}
      />
    </Flex>
  )
}

export default GiftCardComsumptionList
